import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle, RefreshCw } from 'lucide-react';
import { useJobStream } from '@/hooks/useJobStream';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { JobDetailSkeleton, ReportSection } from './JobDetailSections';
import { JobStatusHeader } from './JobStatusHeader';
import { GoToReviewButton } from './GoToReviewButton';
import { JobMetadataCard } from './JobMetadataCard';
import { AskContractPanel } from './AskContractPanel';
import { SourcePdfCard } from './SourcePdfCard';
import { ResubmitBanner } from './ResubmitBanner';
import { JobCommentsPanel } from './JobCommentsPanel';
import { StageReviewPanel } from './stages/StageReviewPanel';
import { StageHistoryRail } from './stages/StageHistoryRail';
import { ResumePipelinePanel } from './stages/ResumePipelinePanel';
import { fetchReport } from './fetchReport';
import { useAuth } from '@/hooks/useAuth';
import { pausedStage } from '@/types/enums';
import type { Job, AgentResult, GateReport } from '@/types';

// ─── Fetch helpers ───────────────────────────────────────────────────────────

async function fetchJob(id: string): Promise<Job> {
  return api.get<Job>(`/api/v1/jobs/${id}`);
}

async function fetchAgentResults(id: string): Promise<AgentResult[]> {
  return api.get<AgentResult[]>(`/api/v1/jobs/${id}/agents`);
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const jobId = id ?? '';
  const { canReview } = useAuth();

  const [job, setJob] = useState<Job | null>(null);
  const [agentResults, setAgentResults] = useState<AgentResult[]>([]);
  const [report, setReport] = useState<GateReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'report' | 'review'>('report');
  const [commentsKey, setCommentsKey] = useState(0);
  const isNotFound = error === 'not_found';

  const stream = useJobStream(jobId);
  const prevStatusRef = useRef<string | null>(null);

  // Initial data load
  useEffect(() => {
    if (!jobId) return;
    setIsLoading(true);
    setError(null);

    Promise.all([fetchJob(jobId), fetchAgentResults(jobId)])
      .then(([j, ar]) => {
        setJob(j);
        setAgentResults(ar);
        // Fetch the gate report on completed OR failed jobs — the pipeline_runner
        // synthesises a MANUAL_REVIEW_REQUIRED report on graph crash so the
        // reviewer still has something to act on.
        if (j.status === 'completed' || j.status === 'failed') {
          return fetchReport(jobId).then(setReport);
        }
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setError('not_found');
        } else {
          setError('Failed to load job details.');
        }
      })
      .finally(() => { setIsLoading(false); });
  }, [jobId]);

  // React to stream status changes
  useEffect(() => {
    if (!stream.status || stream.status === prevStatusRef.current) return;
    prevStatusRef.current = stream.status;

    if (stream.status === 'completed' || stream.status === 'failed') {
      Promise.all([fetchJob(jobId), fetchAgentResults(jobId)])
        .then(([j, ar]) => {
          setJob(j);
          setAgentResults(ar);
          if (j.status === 'completed' || j.status === 'failed') {
            return fetchReport(jobId).then(setReport);
          }
        })
        .catch(() => { /* keep showing last known data */ });
    }
  }, [stream.status, jobId]);

  // While the per-stage HIL pipeline is mid-flight (review_status starts with
  // paused_at_ OR equals in_progress between approvals), poll the job AND the
  // per-agent results list every 3 s so the StageHistoryRail picks up newly
  // approved stages and the StageReviewPanel transitions to the next pause.
  useEffect(() => {
    if (!job) return;
    const rs = job.review_status ?? '';
    const isActive = rs.startsWith('paused_at_') || rs === 'in_progress';
    if (!isActive) return;
    const id = setInterval(() => {
      Promise.all([fetchJob(jobId), fetchAgentResults(jobId)])
        .then(([j, ar]) => { setJob(j); setAgentResults(ar); })
        .catch(() => { /* ignore transient errors */ });
    }, 3000);
    return () => { clearInterval(id); };
  }, [job, jobId]);

  function handleJobMutation() {
    fetchJob(jobId)
      .then(setJob)
      .catch(() => { /* ignore */ });
  }

  if (isLoading) return <JobDetailSkeleton />;

  if (isNotFound) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-2xl font-bold">Job not found</h1>
        <p className="text-muted-foreground text-sm">This job does not exist or you don't have access to it.</p>
        <Button variant="outline" asChild>
          <Link to="/dashboard"><ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />Back to Dashboard</Link>
        </Button>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">{error ?? 'An unexpected error occurred.'}</p>
        <Button variant="outline" onClick={() => { window.location.reload(); }}>
          <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />Retry
        </Button>
      </div>
    );
  }

  const showReport = (job.status === 'completed' || job.status === 'failed') && report != null;
  const paused = pausedStage(job.review_status);
  const terminalReview = job.review_status === 'pending_review' || job.review_status === 'reviewed';
  // Cancelled is no longer terminal — the reviewer can Recall the
  // pipeline if at least one stage was approved before the cancel.
  const terminalJob = ['completed', 'failed'].includes(job.status);
  const isRecallable = job.status === 'cancelled' && agentResults.some(
    (r) => r.review_state === 'approved' || r.review_state === 'edited_approved',
  );
  const showResumePanel = !paused && !terminalReview && (!terminalJob || isRecallable);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div>
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring rounded mb-4">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Dashboard
        </Link>
        <JobStatusHeader
          job={job}
          onCancelled={handleJobMutation}
          onRetried={handleJobMutation}
        />
        {job.status === 'completed' && (
          <div className="mt-3">
            <GoToReviewButton jobId={job.id} />
          </div>
        )}
      </div>

      <ResubmitBanner
        parentJobId={job.parent_job_id}
        childJobId={job.child_job_id}
        version={job.version}
        currentJobId={job.id}
      />

      {/* Side-by-side: agent review left, PDF + chat right (5:4 on lg). */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-9">
        <div className="lg:col-span-5 space-y-6">
          <StageHistoryRail agentResults={agentResults} currentPausedStage={paused} />

          {paused && (
            <StageReviewPanel
              jobId={job.id}
              stage={paused}
              onApproved={handleJobMutation}
              onActivity={() => { setCommentsKey((n) => n + 1); }}
            />
          )}

          {/* Stall-recovery panel — mid-flight but no active stage paused.
              On cancelled jobs with prior progress this becomes the
              Recall surface (same endpoint, different copy). */}
          {showResumePanel && (
            <ResumePipelinePanel
              jobId={job.id}
              reviewStatus={job.review_status}
              jobStatus={job.status}
              onResumed={handleJobMutation}
            />
          )}

          {!paused && showReport && (
            <ReportSection
              job={job}
              report={report}
              activeTab={activeTab}
              canReview={canReview}
              onTabChange={setActiveTab}
              onReviewClaimed={() => { handleJobMutation(); setActiveTab('review'); }}
            />
          )}

        </div>

        <aside aria-label="Source PDF and metadata" className="lg:col-span-4">
          {/* The whole right rail is pinned on large screens and scrolls
              independently of the left column. Its height is capped to the
              viewport with internal overflow so the Ask panel and comments
              stay reachable — a full-height sticky PDF used to overlap them
              and swallow pointer events, making the Ask box unusable. */}
          <div className="space-y-4 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1">
            {/* Source PDF — pinned beside the agent review panels so the
                reviewer can cross-check every finding against the contract
                text without leaving the page. */}
            <SourcePdfCard
              jobId={job.id}
              onTranslated={() => { setCommentsKey((n) => n + 1); }}
            />
            <AskContractPanel jobId={job.id} />
            {/* Comments &amp; escalations — every comment ever raised on
                this job. Surfaces what previous reviewers flagged so the
                current reviewer doesn't miss prior context. */}
            <JobCommentsPanel jobId={job.id} refreshKey={commentsKey} />
            {/* Job details — collapsed by default. File metadata, decisions,
                and lineage are visible on demand without taking permanent
                space from the PDF + chat. */}
            <details className="rounded-lg border bg-card">
              <summary className="cursor-pointer list-none flex items-center justify-between px-4 py-2 text-sm font-medium hover:bg-muted/50 rounded-lg">
                <span>Job details</span>
                <span className="text-xs text-muted-foreground">expand</span>
              </summary>
              <div className="px-2 pb-2 pt-1 border-t">
                <JobMetadataCard job={job} />
              </div>
            </details>
          </div>
        </aside>
      </div>
    </div>
  );
}
